import React from "react";
import { ui } from "../lib/telemetry/instrument.js";
import { C, themeColors } from "../lib/colors.js";
import { IconAlert, IconChevronDown, IconClose, IconSignals } from "./ui/Icons.jsx";
import { SIGNAL_BAR_IN_COLUMN_Z, SIGNAL_BAR_Z } from "../lib/drawerStack.js";
import { AccountIcon, BugIcon, SettingsIcon } from "./UtilityIcons.jsx";
import { surface } from "../lib/telemetry/instrument.js";
import { bugReportShortcutLabel } from "../lib/bugReportTriggers.js";

// The toggle's hover and focus states need real CSS.
const BAR_CSS = `
.sy-signals-toggle{display:inline-flex;align-items:center;gap:6px;height:24px;padding:0 8px;margin-left:-4px;border:0;border-radius:6px;background:transparent;color:var(--c-grayL);font:inherit;font-size:12px;font-weight:500;cursor:pointer;flex-shrink:0;}
.sy-signals-toggle:hover{background:var(--c-rowInactive);color:var(--c-white);}
.sy-signals-toggle:focus-visible{outline:2px solid var(--c-accent);outline-offset:1px;}
.sy-signals-toggle svg{width:14px;height:14px;}
`;

function IndicesBar({
  theme,
  series,
  onRemove,
  proColors: _proColors,
  onAdd: _onAdd,
  onAccountToggle,
  accountOpen,
  onBugReport,
  // True while a z400 overlay (the report modal, the preprocess-confirmation panel) is
  // open. Raises the bar over it so the icon cluster stays full-strength and clickable,
  // and makes everything else on the bar inert — see the container below.
  liftedOverIcons = false,
  onSettingsToggle,
  settingsOpen,
  tsDrawerOpen,
  onTsDrawerToggle,
  bugFlash,
  // The navigation-rail layout carries these icons on the rail instead.
  hideUtilityIcons = false,
  // At the foot of the timeline column (rail layout): behind the rail's panels and every overlay.
  inColumn = false,
  // The rail panel's width while it is open over this column (a CSS length or px), so the
  // Signals button and the chosen signals start clear of it instead of under it.
  leftInset = 0,
}) {
  const inset = typeof leftInset === "number" ? `${leftInset}px` : leftInset;
  // Bar itself toggles the drawer (chips/icons stop propagation). Bar sits above the drawer
  // (SIGNAL_BAR_Z, above every drawer slot) so the drawer slides up FROM BEHIND the bar.
  //
  // `liftedOverIcons` raises the bar ABOVE the z400 overlays (report modal, preprocess
  // panel) instead. It used to sit below them, which meant the bug / settings / account
  // icons were dimmed by the backdrop and unclickable while a report was open — worked
  // around by a second floating "Report a Bug" button, now deleted. Lifted, the bar itself
  // goes `pointerEvents: none` and only the icon cluster opts back in: the icons work, while
  // a click anywhere else on the strip passes through to the overlay's backdrop and closes
  // it, exactly as a click outside the panel did before. 450 is deliberately under the bug
  // modal's own 600, so opening it still covers the bar.
  return (
    <div
      data-testid="indices-bar"
      {...surface("indices-bar")}
      data-tour-spotlight="tab"
      data-drawer-toggle="tsd"
      {...ui("indices-bar.toggle-drawer")}
      onClick={liftedOverIcons ? undefined : onTsDrawerToggle}
      style={{
        background: C.sidebarBg,
        borderTop: `1px solid ${C.edge}`,
        padding: "4px 12px",
        paddingLeft: `calc(${inset} + 12px)`,
        transition: "padding-left var(--rail-slide)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
        minHeight: 32,
        boxSizing: "border-box",
        cursor: "pointer",
        userSelect: "none",
        position: "relative",
        zIndex: inColumn ? SIGNAL_BAR_IN_COLUMN_Z : liftedOverIcons ? 450 : SIGNAL_BAR_Z,
        pointerEvents: liftedOverIcons ? "none" : "auto",
      }}
    >
      <style>{BAR_CSS}</style>
      <button
        type="button"
        className="sy-signals-toggle"
        aria-expanded={!!tsDrawerOpen}
        aria-label={tsDrawerOpen ? "Hide signals" : "Show signals"}
        {...ui("indices-bar.toggle-drawer")}
        onClick={(e) => {
          e.stopPropagation();
          if (!liftedOverIcons) onTsDrawerToggle();
        }}
      >
        <IconSignals />
        <span>Signals</span>
        <IconChevronDown
          style={{
            transform: tsDrawerOpen ? "rotate(180deg)" : "none",
            transition: "transform .2s ease",
          }}
        />
      </button>
      {Object.keys(series).length === 0 && (
        <span style={{ fontSize: 12, color: C.gray }}>
          Add a signal to see it next to your forecast
        </span>
      )}
      {Object.entries(series).map(([k, s], si) => {
        // The slot colour marks the line on the chart; the name stays in text colour.
        const tc = themeColors(theme);
        const unavailable = !!s.unavailable;
        const sc = unavailable
          ? tc.orange
          : (theme === "dev" || theme === "dusk" ? [tc.accent, tc.white] : ["#33ddcc", tc.orange])[
              si
            ] || (theme === "dev" || theme === "dusk" ? tc.action : s.color);
        return (
          <div
            key={k}
            {...ui("indices-bar.series-chip", s.ssid)}
            className="sy-token"
            onClick={(e) => e.stopPropagation()}
            title={unavailable ? s.unavailableReason : undefined}
            style={{ cursor: "default", gap: 6, padding: "3px 4px 3px 8px", color: C.grayL }}
          >
            {unavailable ? (
              <IconAlert style={{ color: sc, width: 12, height: 12 }} />
            ) : (
              <span
                aria-hidden="true"
                style={{ width: 8, height: 8, borderRadius: 4, background: sc, flexShrink: 0 }}
              />
            )}
            <span>{k}</span>
            <button
              type="button"
              className="sy-iconbtn"
              aria-label={`Remove ${k}`}
              {...ui("indices-bar.series-remove", s.ssid)}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(k);
              }}
              style={{ width: 18, height: 18, borderRadius: 4, padding: 0 }}
            >
              <IconClose style={{ width: 12, height: 12 }} />
            </button>
          </div>
        );
      })}
      <div style={{ flex: 1 }} />
      {/* The bug / settings / user cluster. In the navigation-rail layout these move to the
         bottom of the rail, so the bar drops them and its divider. */}
      {!hideUtilityIcons && (
        <>
          {/* Visible vertical divider between time-series area and the bug/settings/user cluster. */}
          <div
            style={{
              width: 1,
              alignSelf: "stretch",
              margin: "0 10px",
              // hex-alpha (`gray` + `66`) needs a raw value, not a var() string.
              background: `${themeColors(theme).gray}66`,
              flexShrink: 0,
            }}
          />
          <div
            {...ui("indices-bar.utility-icons")}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
              marginRight: -6,
              cursor: "default",
              // The one part of the bar that stays live when the bar is lifted over an overlay
              // (see the container above): these three are the controls a user needs to reach
              // WITH a report or preprocess panel open, and the reason the lift exists.
              pointerEvents: "auto",
            }}
          >
            <BugIcon
              theme={theme}
              flash={bugFlash}
              onClick={onBugReport}
              data-tour-spotlight="bug-icon"
              title={`Send feedback (${bugReportShortcutLabel()})`}
              {...ui("indices-bar.bug-report")}
            />
            <SettingsIcon
              theme={theme}
              open={settingsOpen}
              data-settings-toggle
              data-tour-spotlight="settings-gear"
              onClick={onSettingsToggle}
              title="Settings"
              {...ui("indices-bar.settings")}
            />
            <AccountIcon
              theme={theme}
              open={accountOpen}
              data-account-toggle
              onClick={onAccountToggle}
              title="Account"
              {...ui("indices-bar.account")}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default IndicesBar;
